import { useState, useRef, useEffect } from "react";
import { useGT } from "gt-react";
import { LogOut, User } from "lucide-react";
import { useAuthStore } from "../authStore";
import { initiateLogin, setPendingSignedInWorkspace } from "../auth";
import { logout as doLogout } from "../auth";
import { disableSignedInWorkspace, refreshRuntimeStatus } from "../store";
import { startRuntimeLogin } from "../runtimeClient";
import { PlanBadge } from "./PlanBadge";
import { toast } from "./Toasts";

const isDesktop = window.location.protocol === "file:";

export function AccountMenu() {
  const t = useGT();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [open, setOpen] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  // Poll runtime auth status after opening browser for login
  useEffect(() => {
    if (!loggingIn) return;
    const interval = setInterval(async () => {
      await refreshRuntimeStatus();
    }, 2000);
    return () => clearInterval(interval);
  }, [loggingIn]);

  const handleLogout = async () => {
    setOpen(false);
    try {
      await doLogout();
      clearAuth();
      await disableSignedInWorkspace();
      toast(t("Signed out"), "success");
    } catch {
      clearAuth();
    }
  };

  const handleSignIn = async () => {
    if (isDesktop) {
      try {
        setLoggingIn(true);
        const { url } = await startRuntimeLogin();
        window.open(url, "_blank");
        toast(t("Sign in via your browser, then return here"), "info");
      } catch (error) {
        toast(error instanceof Error ? error.message : "Login failed", "error");
        setLoggingIn(false);
      }
    } else {
      setPendingSignedInWorkspace();
      void initiateLogin();
    }
  };

  // Stop polling once authenticated
  useEffect(() => {
    if (loggingIn && isAuthenticated) setLoggingIn(false);
  }, [loggingIn, isAuthenticated]);

  if (!isAuthenticated || !user) {
    if (isDesktop) return null;
    return (
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => void handleSignIn()}
          className="rounded px-2 py-1 text-[11px] font-medium text-zinc-400 hover:bg-surface-raised hover:text-zinc-200"
        >
          {t("Sign In")}
        </button>
        <button
          onClick={() => void handleSignIn()}
          className="rounded bg-brand px-2.5 py-1 text-[11px] font-semibold text-zinc-900 hover:bg-brand-light"
        >
          {t("Create Account")}
        </button>
      </div>
    );
  }

  // Signed in: avatar + name + dropdown
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded px-1.5 py-1 text-xs text-zinc-400 hover:bg-surface-raised hover:text-zinc-200"
        title={user.email}
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
        ) : (
          <User size={14} />
        )}
        <span className="max-w-[120px] truncate">{user.displayName}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-lg border border-border bg-surface-raised py-1 shadow-xl">
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-[10px] text-zinc-600">{user.email}</span>
            <PlanBadge />
          </div>
          <div className="my-1 border-t border-border" />
          <button
            onClick={() => void handleLogout()}
            className="flex w-full items-center justify-start gap-2 px-3 py-1.5 text-left text-xs text-zinc-400 hover:bg-surface-overlay hover:text-zinc-200"
          >
            <LogOut size={12} />
            <span>{t("Sign Out")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
